// ModeSelection.jsx - Triple-Gated Mode Selection
// This choice is PERMANENT. One save. One airline. One mode.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
// Icons removed - text-only UI

// ============================================================================
// GATE 1: PLEASE READ THIS FIRST
// ============================================================================
function Gate1Acknowledgment({ onProceed }) {
  const [canProceed, setCanProceed] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    // 3-second countdown before button enables
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanProceed(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center">
        {/* Warning Icon */}
        <div className="mb-8 text-amber-500 text-6xl">
          ⚠
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 tracking-wide">
          PLEASE READ THIS FIRST
        </h1>

        {/* Divider */}
        <div className="w-full h-px bg-gray-800 mb-8" />

        {/* Warning Text */}
        <div className="space-y-4 text-gray-300 mb-8">
          <p className="text-xl font-semibold text-white">
            This choice is PERMANENT.
          </p>
          <p className="text-lg">
            You will have ONE save. ONE airline. ONE mode.
          </p>
          <p className="text-lg text-gray-400">
            There is no going back.
          </p>
          <p className="text-xl font-semibold text-amber-500 mt-6">
            Choose wisely.
          </p>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-800 mb-8" />

        {/* Button */}
        <button
          onClick={onProceed}
          disabled={!canProceed}
          className={`
            px-8 py-4 text-sm tracking-widest font-semibold
            border-2 transition-all duration-300
            ${canProceed
              ? 'border-white text-white hover:bg-white hover:text-black cursor-pointer'
              : 'border-gray-700 text-gray-600 cursor-not-allowed'
            }
          `}
        >
          {canProceed ? 'I UNDERSTAND' : `WAIT ${countdown}...`}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// GATE 2: THE BREAKDOWN - Mode Comparison
// ============================================================================
function Gate2Comparison({ onSelectMode }) {
  const [hoveredMode, setHoveredMode] = useState(null);

  const comparisonData = [
    { label: 'Pace', simple: 'Fast, arcadey', simulation: 'Methodical, real' },
    { label: 'Flights', simple: 'One-click, instant', simulation: 'Preflight checklists' },
    { label: 'Crew', simple: 'Automatic', simulation: 'Manual (FAA regs)' },
    { label: 'Aircraft', simple: 'Purchase only', simulation: 'Purchase or lease' },
    { label: 'Airport Slots', simple: 'Fast progression', simulation: 'Earn through negotiation' },
    { label: 'Time Compression', simple: '2x (4x with EOS)', simulation: '2x (4x with EOS)' },
    { label: 'EOS Requirement', simple: '7 consecutive days', simulation: '7 consecutive days' },
    { label: 'Exclusive Content', simple: 'Rare, Relic, Anomalous, Legendary rolls', simulation: 'Rare, Relic, Anomalous, Legendary + MYTHIC rolls' },
    { label: 'Difficulty', simple: 'Accessible', simulation: 'Extraordinarily realistic (well, not counting the rolls)' },
  ];

  return (
    <div className="fixed inset-0 bg-black overflow-y-auto">
      <div className="min-h-full flex flex-col items-center justify-start py-8 px-4">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8 tracking-wide">
          CHOOSE YOUR PATH
        </h1>

        {/* Mode Headers */}
        <div className="flex gap-8 mb-8 flex-wrap justify-center">
          <div className="w-64 text-center p-6 border border-green-500/30 bg-green-500/5">
            <h2 className="text-2xl font-bold text-green-400 mb-2">SIMPLE MODE</h2>
            <p className="text-gray-400 text-sm italic">"The Empire Builder"</p>
          </div>
          <div className="w-64 text-center p-6 border border-blue-500/30 bg-blue-500/5">
            <h2 className="text-2xl font-bold text-blue-400 mb-2">SIMULATION MODE</h2>
            <p className="text-gray-400 text-sm italic">"The Real Thing"</p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="w-full max-w-4xl mb-8">
          <div className="border border-gray-800 overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-gray-900/50 border-b border-gray-800">
              <div className="p-4 text-gray-500 font-semibold text-sm"></div>
              <div className="p-4 text-green-400 font-semibold text-sm text-center border-x border-gray-800">SIMPLE MODE</div>
              <div className="p-4 text-blue-400 font-semibold text-sm text-center">SIMULATION MODE</div>
            </div>
            {/* Table Rows */}
            {comparisonData.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-black' : 'bg-gray-900/20'} ${i < comparisonData.length - 1 ? 'border-b border-gray-800' : ''}`}
              >
                <div className="p-4 text-gray-400 text-sm font-medium">{row.label}</div>
                <div className="p-4 text-gray-300 text-sm text-center border-x border-gray-800">{row.simple}</div>
                <div className="p-4 text-gray-300 text-sm text-center">{row.simulation}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Descriptions */}
        <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 mb-8">
          {/* Simple Mode Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-green-400 border-b border-green-400/30 pb-2">SIMPLE MODE</h3>
            <p className="text-gray-400 text-sm">
              You click, planes fly, money comes in. Build your empire while doing other things.
            </p>

            <div className="mt-4">
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Best For:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>- Casual players</li>
                <li>- Idle game fans</li>
                <li>- Empire builders</li>
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-green-400 text-xs uppercase tracking-wide mb-2">Pros:</p>
              <ul className="text-gray-300 text-sm space-y-1">
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Fast progression</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Less micromanagement</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Play while multitasking</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> All airports open immediately</li>
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-red-400 text-xs uppercase tracking-wide mb-2">Cons:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> No Mythic tier</li>
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> 10-day EOS grind</li>
              </ul>
            </div>
          </div>

          {/* Simulation Mode Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-blue-400 border-b border-blue-400/30 pb-2">SIMULATION MODE</h3>
            <p className="text-gray-400 text-sm">
              You run an airline. Every crew, every checklist, every delay. The real thing.
            </p>

            <div className="mt-4">
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Best For:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>- Flight sim enthusiasts</li>
                <li>- Challenge seekers</li>
                <li>- Actual airline ops professionals</li>
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-green-400 text-xs uppercase tracking-wide mb-2">Pros:</p>
              <ul className="text-gray-300 text-sm space-y-1">
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Mythic tier access</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Deeper gameplay</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Shorter EOS requirement (10 days)</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Real operational challenge</li>
                <li className="flex items-center gap-2"><span className="text-green-400">✓</span> Bragging rights</li>
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-red-400 text-xs uppercase tracking-wide mb-2">Cons:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Steep learning curve</li>
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Requires full attention</li>
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Must earn every slot</li>
                <li className="flex items-center gap-2"><span className="text-red-400">✗</span> Crew violations can ruin your day</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Selection Buttons */}
        <div className="flex gap-8 flex-wrap justify-center">
          <button
            onClick={() => onSelectMode('simple')}
            onMouseEnter={() => setHoveredMode('simple')}
            onMouseLeave={() => setHoveredMode(null)}
            className={`
              px-8 py-4 text-sm tracking-widest font-semibold
              border-2 transition-all duration-300
              ${hoveredMode === 'simple'
                ? 'border-green-400 text-black bg-green-400'
                : 'border-green-400/50 text-green-400 hover:border-green-400'
              }
            `}
          >
            CHOOSE SIMPLE
          </button>
          <button
            onClick={() => onSelectMode('simulation')}
            onMouseEnter={() => setHoveredMode('simulation')}
            onMouseLeave={() => setHoveredMode(null)}
            className={`
              px-8 py-4 text-sm tracking-widest font-semibold
              border-2 transition-all duration-300
              ${hoveredMode === 'simulation'
                ? 'border-blue-400 text-black bg-blue-400'
                : 'border-blue-400/50 text-blue-400 hover:border-blue-400'
              }
            `}
          >
            CHOOSE SIMULATION
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GATE 3: ARE YOU SURE? - Final Confirmation
// ============================================================================
function Gate3Confirmation({ selectedMode, onConfirm, onGoBack }) {
  const [hoveredButton, setHoveredButton] = useState(null);

  const isSimple = selectedMode === 'simple';

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 tracking-wide">
          ARE YOU SURE?
        </h1>

        {/* Divider */}
        <div className="w-full h-px bg-gray-800 mb-8" />

        {/* Mode-specific message */}
        <div className="space-y-4 text-gray-300 mb-8">
          <p className="text-xl font-semibold" style={{ color: isSimple ? '#4ade80' : '#60a5fa' }}>
            You selected {isSimple ? 'SIMPLE' : 'SIMULATION'} MODE.
          </p>

          <p className="text-gray-400">
            This cannot be undone.
          </p>

          {isSimple ? (
            <>
              <p className="text-gray-300">
                You will not have access to the <span className="text-purple-400 font-semibold">Mythic tier</span>.
              </p>
              <p className="text-gray-400 mt-4">
                Your airline will be fast and fun - but some doors will be permanently closed.
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-300">
                Your airline will live or die by your operational decisions.
              </p>
              <p className="text-gray-400">
                Every crew legality. Every weather delay. Every slot negotiation.
              </p>
              <p className="text-gray-300 font-medium mt-4">
                This is the real thing.
              </p>
              <p className="text-purple-400 font-semibold mt-4">
                The Mythic awaits - if you can reach it.
              </p>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gray-800 mb-8" />

        {/* Buttons */}
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={onGoBack}
            onMouseEnter={() => setHoveredButton('back')}
            onMouseLeave={() => setHoveredButton(null)}
            className={`
              px-8 py-4 text-sm tracking-widest font-semibold
              border-2 transition-all duration-300
              ${hoveredButton === 'back'
                ? 'border-gray-400 text-black bg-gray-400'
                : 'border-gray-600 text-gray-400 hover:border-gray-400'
              }
            `}
          >
            GO BACK
          </button>
          <button
            onClick={onConfirm}
            onMouseEnter={() => setHoveredButton('confirm')}
            onMouseLeave={() => setHoveredButton(null)}
            className={`
              px-8 py-4 text-sm tracking-widest font-semibold
              border-2 transition-all duration-300
              ${hoveredButton === 'confirm'
                ? isSimple
                  ? 'border-green-400 text-black bg-green-400'
                  : 'border-blue-400 text-black bg-blue-400'
                : isSimple
                  ? 'border-green-400/50 text-green-400 hover:border-green-400'
                  : 'border-blue-400/50 text-blue-400 hover:border-blue-400'
              }
            `}
          >
            {isSimple ? "YES, LET'S BUILD" : "YES, I'M READY"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT - Simple mode only (Simulation mode archived for post-launch)
// ============================================================================
export default function ModeSelection() {
  const navigate = useNavigate();
  const { setGameMode } = useWorldState();

  // Auto-select Simple mode on mount (Simulation mode archived)
  useEffect(() => {
    // Set Simple mode permanently
    setGameMode('simple');
    localStorage.setItem('modeSelected', 'true');
    localStorage.setItem('gameMode', 'simple');

    // Navigate to livery designer
    navigate("/livery");
  }, [setGameMode, navigate]);

  // Show loading while redirecting
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl font-bold mb-4 animate-pulse" style={{ color: "#60ffd0" }}>---</div>
        <p style={{ color: "#737373" }}>Starting game...</p>
      </div>
    </div>
  );

  // Original gate system archived - see src/archive/simulation/
  // Gate 1: Acknowledgment, Gate 2: Mode comparison, Gate 3: Confirmation
}
